import React, { useMemo, useState } from "react";
import { History } from "lucide-react";
import { formatCoinPoolCoin } from "../../utils/coinPoolHelpers";
import BondCoinHistoryList from "../bond-coin/BondCoinHistoryList";
import CoinPoolOrdersList from "./CoinPoolOrdersList";

const TABS = [
  { id: "orders", label: "Orders" },
  { id: "rounds", label: "Rounds" },
];

const BondCoinPoolSidePanel = ({
  round,
  userId,
  actionLoading,
  onCancelOrder,
}) => {
  const [tab, setTab] = useState("orders");

  const poolSplit = useMemo(() => {
    const head = Math.max(0, Number(round?.headStaked) || 0);
    const tail = Math.max(0, Number(round?.tailStaked) || 0);
    const total = head + tail;
    if (total <= 0) {
      return { headPct: 50, tailPct: 50, isEmpty: true };
    }
    return {
      headPct: Math.round((head / total) * 100),
      tailPct: Math.round((tail / total) * 100),
      isEmpty: false,
    };
  }, [round?.headStaked, round?.tailStaked]);

  const totalStaked = Number(round?.totalStaked) || 0;
  const entryCount = Number(round?.entryCount) || 0;
  const orderCount = round?.orders?.length ?? 0;
  const waitingTotal =
    (round?.book?.openHeadTotal ?? 0) + (round?.book?.openTailTotal ?? 0);

  return (
    <aside className="bplay-games-rail bcoin-side-rail" aria-label="Coin Pool">
      <div className="bcoin-side-rail-scroll bcoin-pool-rail">
        <div className="bcoin-round-overview">
          <header className="bcoin-panel-section-head flush">
            <span className="bcoin-panel-section-title">
              <History size={13} strokeWidth={2.5} />
              Round
            </span>
            <span className="bcoin-panel-section-count">{entryCount} in pool</span>
          </header>
          <strong className="bcoin-round-total tabular-nums">
            {formatCoinPoolCoin(totalStaked)} <em>matched</em>
          </strong>
          {waitingTotal > 0 ? (
            <p className="bcoin-ob-round-waiting tabular-nums">
              + {formatCoinPoolCoin(waitingTotal)} in book
            </p>
          ) : null}
          <div
            className={`bcoin-round-split-bar${poolSplit.isEmpty ? " is-empty" : ""}`}
            role="img"
            aria-label={`Head ${poolSplit.headPct}%, Tail ${poolSplit.tailPct}%`}
          >
            <span
              className="bcoin-round-split-head"
              style={{ width: `${poolSplit.headPct}%` }}
              title={`Head ${poolSplit.headPct}%`}
            />
            <span
              className="bcoin-round-split-tail"
              style={{ width: `${poolSplit.tailPct}%` }}
              title={`Tail ${poolSplit.tailPct}%`}
            />
          </div>
          <div className="bcoin-round-split-legend">
            <span>
              <span className="bcoin-pool-dot head" />
              Head {poolSplit.headPct}%
            </span>
            <span>
              <span className="bcoin-pool-dot tail" />
              Tail {poolSplit.tailPct}%
            </span>
          </div>
        </div>

        <div className="bcoin-panel-section bcoin-pool-rail-panel">
          <div className="bcoin-rail-tabs" role="tablist" aria-label="Pool panel">
            {TABS.map(({ id, label }) => {
              const active = tab === id;
              const count = id === "orders" ? orderCount : null;
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`bcoin-rail-tab${active ? " active" : ""}`}
                  onClick={() => setTab(id)}
                >
                  {label}
                  {count != null && count > 0 ? (
                    <span className="bcoin-rail-tab-count">{count}</span>
                  ) : null}
                </button>
              );
            })}
          </div>

          <div className="bcoin-rail-tab-panel" role="tabpanel">
            {tab === "orders" ? (
              <CoinPoolOrdersList
                embedded
                round={round}
                userId={userId}
                actionLoading={actionLoading}
                onCancelOrder={onCancelOrder}
              />
            ) : (
              <div className="bcoin-rail-round-tab">
                <BondCoinHistoryList game="pool" limit={6} />
              </div>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default BondCoinPoolSidePanel;
